import { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { AdditionalMaterial } from './CreateLesson';
import { useTranslation } from 'react-i18next';
import { error } from 'console';
import { FromHeading } from '../../../../constants';

type AddAdditionalMaterialFormProps = {
  addMaterial: (material: any) => void;
  close: () => void;
};

const AddAdditionalMaterialForm = ({ addMaterial, close }: AddAdditionalMaterialFormProps) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Partial<AdditionalMaterial>>({
    title: '',
    title_uk: '',
    title_ru: '',
    file: null,
    url: '',
    kind: 'link',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof AdditionalMaterial, string>>>({});

  const setField = <K extends keyof AdditionalMaterial>(k: K, v: AdditionalMaterial[K]) =>
    setFormData((prev) => ({ ...prev, [k]: v }));

  const isValidUrl = (s: string) => {
    if (!s) return false;
    try {
      new URL(s);
      return true;
    } catch {
      return false;
    }
  };

  const validate = () => {
    const e: Partial<Record<keyof AdditionalMaterial, string>> = {};

    if (!formData.title?.trim())
      e.title = t('courses.lessons.lessonForm.fieldRequired') || 'Required';
    if (!formData.title_uk?.trim())
      e.title_uk = t('courses.lessons.lessonForm.fieldRequired') || 'Required';
    if (!formData.title_ru?.trim())
      e.title_ru = t('courses.lessons.lessonForm.fieldRequired') || 'Required';

    if (formData.kind === 'link') {
      if (!formData.url?.trim())
        e.url = t('courses.lessons.lessonForm.fieldRequired') || 'Required';
      else if (!isValidUrl(formData.url))
        e.url = t('courses.lessons.lessonForm.invalidUrl') || 'Invalid URL';
    } else {
      if (!formData.file) e.file = t('courses.lessons.lessonForm.fileRequired') || 'File required';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const clearError = (k: keyof AdditionalMaterial) =>
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  const handleSubmit = () => {
    if (!validate()) return;
    addMaterial(formData);
  };
  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
     <Card className='p-3'>
        
          <CardTitle tag="h5" className="mb-4">
            {t('courses.lessons.lessonForm.addMaterialsTitle')}
          </CardTitle>
          <Row>
            <Col sm="6">
              {' '}
              <Label className="d-flex flex-column w-100 mb-3">
                {t('courses.lessons.lessonForm.titleEn')}
                <Input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setField('title', e.target.value)}
                  onBlur={() => {
                    if (!formData.title?.trim())
                      setErrors((prev) => ({
                        ...prev,
                        title: t('courses.lessons.lessonForm.fieldRequired') || 'Required',
                      }));
                  }}
                  invalid={!!errors.title}
                  onFocus={() => clearError('title')}
                />
                <FormFeedback>{errors.title}</FormFeedback>
              </Label>
              <Label className="d-flex flex-column w-100 mb-3">
                {t('courses.lessons.lessonForm.titleUk')}
                <Input
                  type="text"
                  required
                  value={formData.title_uk}
                  onChange={(e) => setField('title_uk', e.target.value)}
                  onBlur={() => {
                    if (!formData.title_uk?.trim())
                      setErrors((prev) => ({
                        ...prev,
                        title_uk: t('courses.lessons.lessonForm.fieldRequired') || 'Required',
                      }));
                  }}
                  invalid={!!errors.title_uk}
                  onFocus={() => clearError('title_uk')}
                />
                <FormFeedback>{errors.title_uk}</FormFeedback>
              </Label>
              <Label className="d-flex flex-column w-100 mb-3">
                {t('courses.lessons.lessonForm.titleRu')}
                <Input
                  type="text"
                  required
                  value={formData.title_ru}
                  onChange={(e) => setField('title_ru', e.target.value)}
                  onBlur={() => {
                    if (!formData.title_ru?.trim())
                      setErrors((prev) => ({
                        ...prev,
                        title_ru: t('courses.lessons.lessonForm.fieldRequired') || 'Required',
                      }));
                  }}
                  onFocus={() => clearError('title_ru')}
                  invalid={!!errors.title_ru}
                />
                <FormFeedback>{errors.title_ru}</FormFeedback>
              </Label>
            </Col>
            <Col sm="6">
              <Label>
                {t('courses.lessons.lessonForm.fileOrUrl')}
                <Row className="mt-3">
                  <Col sm="6">
                    <FormGroup check key={'file'} className={`radio radio-${'primary'}`}>
                      <Input
                        type="radio"
                        name={'select_file'}
                        id={`radio-file`}
                        value={'file'}
                        checked={formData.kind === 'file'}
                        onChange={() => {
                          setField('kind', 'file');
                          setErrors((prev) => ({ ...prev, url: undefined }));
                        }}
                      />
                      <Label check htmlFor={`radio-file`}>
                        {t('courses.lessons.lessonForm.file')}
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col sm="6">
                    <FormGroup check key={'link'} className={`radio radio-${'primary'}`}>
                      <Input
                        type="radio"
                        name={'select_link'}
                        id={`radio-link`}
                        value={'link'}
                        checked={formData.kind === 'link'}
                        onChange={() => {
                          setField('kind', 'link');
                          setErrors((prev) => ({ ...prev, file: undefined }));
                        }}
                      />
                      <Label check htmlFor={`radio-link`}>
                        {t('courses.lessons.lessonForm.url')}
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
              </Label>
              <Label>
                {t('courses.lessons.lessonForm.file')}
                <Input
                  type="file"
                  disabled={formData.kind === 'link'}
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    setField('file', f);
                    if (f) setErrors((prev) => ({ ...prev, file: undefined }));
                  }}
                  invalid={!!errors.file}
                  onFocus={() => clearError('file')}
                />
                <FormFeedback>{errors.file}</FormFeedback>
              </Label>
              <Label className="d-flex flex-column w-100 mb-3">
                {t('courses.lessons.lessonForm.url')}
                <Input
                  type="text"
                  value={formData.url}
                  disabled={formData.kind === 'file'}
                  onChange={(e) => {
                    setField('url', e.target.value);
                    if (e.target.value) setErrors((prev) => ({ ...prev, url: undefined }));
                  }}
                  onBlur={() => {
                    if (formData.kind === 'link') {
                      if (!formData.url?.trim())
                        setErrors((prev) => ({
                          ...prev,
                          url: t('courses.lessons.lessonForm.fieldRequired') || 'Required',
                        }));
                      else if (!isValidUrl(formData.url))
                        setErrors((prev) => ({
                          ...prev,
                          url: t('courses.lessons.lessonForm.invalidUrl') || 'Invalid URL',
                        }));
                    }
                  }}
                  onFocus={() => clearError('url')}
                  invalid={!!errors.url}
                />
                <FormFeedback>{errors.url}</FormFeedback>
              </Label>
            </Col>
          </Row>
        
      </Card>
      <Row>
        <Col sm="5" className='d-flex justify-content-end'>
          {' '}
          <Button type="submit" color="primary">
            {t('global.ok')}
          </Button>
        </Col>
        <Col sm="5">
          {' '}
          <Button type="button" onClick={close}>
            {t('global.cancel')}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};
export default AddAdditionalMaterialForm;
